import {
  Autocomplete,
  Box,
  List,
  ListItem,
  Popper,
  TextField,
} from '@mui/material';
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { ellipsizeMiddle, IconWrapper } from '@flexo/general';
import { Member, METADATA, RIGHT_DRAWER, Site } from '@flexo/reducers';
import { navigateToCommunity, navigateToMember } from '@flexo/resolvers';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useOnClickOutside } from '@flexo/hooks';
import { navigateToSite } from '../../../../resolvers/src/lib/site-overview-resolver';
import styles from './search.module.scss';
import { adminUserMatchesSearch } from '../admin-users-search.utils';

// Environment for namespacing recent searches storage
const ENV = (import.meta as any).env;

// Progressive-rendering listbox: renders items in chunks to avoid
// DOM jank when there are 500+ entities.
const LISTBOX_INITIAL_COUNT = 40;
const LISTBOX_BATCH_SIZE = 40;

const ProgressiveListbox = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(function ProgressiveListbox(props, ref) {
  const { children, onScroll: parentOnScroll, ...other } = props;
  const items = React.Children.toArray(children);
  const totalCount = items.length;
  const [renderCount, setRenderCount] = useState(LISTBOX_INITIAL_COUNT);

  // Reset when list content changes (e.g. user types / clears search)
  useEffect(() => {
    setRenderCount(LISTBOX_INITIAL_COUNT);
  }, [totalCount]);

  // Auto-expand in batches after first paint: 40 items per frame until
  // all items are in the DOM.  This keeps first paint fast (40 items)
  // while ensuring auto-scroll-to-selected-entity works (~200 ms later).
  useEffect(() => {
    if (renderCount >= totalCount) return;
    const frame = requestAnimationFrame(() => {
      setRenderCount((prev) =>
        Math.min(prev + LISTBOX_BATCH_SIZE, totalCount)
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [renderCount, totalCount]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLUListElement>) => {
      if (parentOnScroll) parentOnScroll(e);
    },
    [parentOnScroll]
  );

  return (
    <ul {...other} ref={ref} onScroll={handleScroll}>
      {items.slice(0, Math.min(renderCount, totalCount))}
    </ul>
  );
});

// Recent searches utilities (LRU, max 3)
type RecentItem = {
  kind: 'community' | 'member' | 'site' | 'ev' | 'wallbox';
  id: string;
  label: string;
  subtitle?: string;
};

function getRecentStorageKey(): string {
  const org = (ENV?.VITE_APP_ORG as string) || 'default';
  return `${org}:search.recent`;
}

function getRecent(): RecentItem[] {
  try {
    const raw = localStorage.getItem(getRecentStorageKey());
    return raw ? (JSON.parse(raw) as RecentItem[]) : [];
  } catch {
    return [];
  }
}

function setRecent(list: RecentItem[]): void {
  try {
    localStorage.setItem(
      getRecentStorageKey(),
      JSON.stringify(list.slice(0, 3))
    );
  } catch {
    // ignore
  }
}

function addRecent(entry: RecentItem): void {
  const list = getRecent().filter(
    (i) => !(i.kind === entry.kind && i.id === entry.id)
  );
  list.unshift(entry);
  setRecent(list);
}

// Responsive helper: use matchMedia instead of reading window.innerWidth repeatedly
const MOBILE_BREAKPOINT_PX = 921;
function useIsMobile(): boolean {
  const getIsMobile = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`).matches;

  const [isMobile, setIsMobile] = useState<boolean>(getIsMobile());

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return;
    }

    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      // Some browsers pass MediaQueryListEvent, older ones call listener with MediaQueryList
      const matches =
        'matches' in e
          ? (e as MediaQueryListEvent).matches
          : (e as MediaQueryList).matches;
      setIsMobile(matches);
    };

    // Set initial value and subscribe
    setIsMobile(mql.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handleChange as (ev: Event) => void);
    } else {
      // Safari/legacy
      mql.addListener(
        handleChange as (this: MediaQueryList, ev: MediaQueryListEvent) => void
      );
    }

    return () => {
      if (typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', handleChange as (ev: Event) => void);
      } else {
        mql.removeListener(
          handleChange as (
            this: MediaQueryList,
            ev: MediaQueryListEvent
          ) => void
        );
      }
    };
  }, []);

  return isMobile;
}

function highlightedText({
  text,
  searchText,
}: {
  text: string;
  searchText: string;
}) {
  if (!searchText) {
    return <>{text}</>;
  }

  const lowercasedText = (text || '').toLowerCase();
  const lowercasedSearchText = (searchText || '').toLowerCase();

  const startIndex = lowercasedText.indexOf(lowercasedSearchText);

  if (startIndex === -1) {
    return <span>{text}</span>;
  }

  const endIndex = startIndex + searchText.length;

  return (
    <>
      {text.slice(0, startIndex)}
      <label>{text.slice(startIndex, endIndex)}</label>
      {text.slice(endIndex)}
    </>
  );
}

function getMemberName(member: any) {
  return `${member?.memberFirstName || ''} ${
    member?.memberLastName || ''
  }`.trim();
}

function filterData(structuredData: any | null, searchText: string) {
  if (!structuredData) {
    return {
      filteredStructuredData: null,
    };
  }

  const lowercasedSearchText = (searchText || '').toLowerCase();

  const filteredCommunities = structuredData.communities
    .map((community) => {
      const filteredMembers = community.members
        .map((member) => {
          // member.sites is already scoped to this community by structureData.
          const filteredSites = member.sites.filter(
            (site) =>
              (site?.siteName || '')
                .toLowerCase()
                .includes(lowercasedSearchText) ||
              (site?.pod || '').toLowerCase().includes(lowercasedSearchText) ||
              (site?.siteAddress || '')
                .toLowerCase()
                .includes(lowercasedSearchText) ||
              (site?.memberId || '')
                .toLowerCase()
                .includes(lowercasedSearchText) ||
              (site?.siteType?.[0]?.siteType || '')
                .toLowerCase()
                .includes(lowercasedSearchText)
          );

          if (
            getMemberName(member)
              .toLowerCase()
              .includes(lowercasedSearchText) ||
            (member?.memberExternalId || '')
              .toLowerCase()
              .includes(lowercasedSearchText) ||
            (member?.memberId || '')
              .toLowerCase()
              .includes(lowercasedSearchText) ||
            (member?.email || '')
              .toLowerCase()
              .includes(lowercasedSearchText) ||
            filteredSites.length > 0
          ) {
            return { ...member, sites: filteredSites };
          }

          return null;
        })
        .filter((member) => member !== null);

      if (
        (community?.communityId || '')
          .toLowerCase()
          .includes(lowercasedSearchText) ||
        (community?.communityName || '')
          .toLowerCase()
          .includes(lowercasedSearchText) ||
        (community?.communityCode || '')
          .toLowerCase()
          .includes(lowercasedSearchText) ||
        (community?.communityExternalId || '')
          .toLowerCase()
          .includes(lowercasedSearchText) ||
        filteredMembers.length > 0
      ) {
        return { ...community, members: filteredMembers };
      }

      return null;
    })
    .filter((community) => community !== null);

  const filteredMembers = structuredData.unassignedMembers.filter(
    (member) =>
      getMemberName(member || '')
        .toLowerCase()
        .includes(lowercasedSearchText) ||
      (member?.meberExternalId || '')
        .toLowerCase()
        .includes(lowercasedSearchText) ||
      (member?.memberId || '').toLowerCase().includes(lowercasedSearchText) ||
      (member?.email || '').toLowerCase().includes(lowercasedSearchText)
  );

  const filteredSites = structuredData.unassignedSites.filter(
    (site) =>
      (site?.communityId || '').toLowerCase().includes(lowercasedSearchText) ||
      (site?.siteType?.[0]?.flexoSiteType || '')
        .toLowerCase()
        .includes(lowercasedSearchText) ||
      (site?.siteName || '').toLowerCase().includes(lowercasedSearchText) ||
      (site?.pod || '').toLowerCase().includes(lowercasedSearchText) ||
      (site?.siteAddress || '').toLowerCase().includes(lowercasedSearchText) ||
      (site?.memberId || '').toLowerCase().includes(lowercasedSearchText) ||
      (site?.siteType?.[0]?.siteType || '')
        .toLowerCase()
        .includes(lowercasedSearchText)
  );

  const filteredEVs = structuredData.unassignedEVs.filter((ev) =>
    (ev?.evId || '').toLowerCase().includes(lowercasedSearchText)
  );

  const filteredWallboxes = structuredData.unassignedWallboxes.filter(
    (wallbox) =>
      (wallbox?.wallboxId || '').toLowerCase().includes(lowercasedSearchText)
  );

  return {
    filteredStructuredData: {
      communities: filteredCommunities.sort((a: any, b: any) => {
        return a.communityName.localeCompare(b.communityName);
      }),
      unassignedMembers: filteredMembers,
      unassignedSites: filteredSites,
      unassignedEVs: filteredEVs,
      unassignedWallboxes: filteredWallboxes,
    },
  };
}

function filterCommunities(
  structuredData: any | null,
  communitiesData: any[],
  searchText: string
) {
  const lowercasedSearchText = (searchText || '').toLowerCase();

  const _filteredStructeredData = filterData(structuredData, searchText);

  const _filteredCommunities = (communitiesData || []).filter(
    (community: any) =>
      (
        _filteredStructeredData as any
      )?.filteredStructuredData?.communities?.some(
        (_filterCom: any) => _filterCom?.communityId === community?.communityId
      )
  );

  return {
    ..._filteredStructeredData,
    filteredCommunities: _filteredCommunities.sort((a: any, b: any) => {
      return (a?.communityName || '').localeCompare(b?.communityName || '');
    }) as any,
  };
}

function filterMembers(
  structuredData: any | null,
  membersData: any,
  searchText: string
) {
  const lowercasedSearchText = (searchText || '').toLowerCase();

  const filterTimeMembers = (members: any) => {
    if (!members || members.length === 0 || Object.keys(members).length === 0) {
      return [];
    }

    return members
      .map((member) => {
        const filteredSites = (member?.sites || []).filter(
          (site) =>
            (site?.siteName || '')
              .toLowerCase()
              .includes(lowercasedSearchText) ||
            (site?.siteId || '').toLowerCase().includes(lowercasedSearchText) ||
            (site?.siteExternalId || '')
              .toLowerCase()
              .includes(lowercasedSearchText) ||
            (site?.pod || '').toLowerCase().includes(lowercasedSearchText) ||
            (site?.address || '')
              .toLowerCase()
              .includes(lowercasedSearchText) ||
            (site?.siteType?.[0]?.siteType || '')
              .toLowerCase()
              .includes(lowercasedSearchText)
        );

        if (
          getMemberName(member || '')
            .toLowerCase()
            .includes(lowercasedSearchText) ||
          filteredSites.length > 0
        ) {
          return { ...member };
        }

        return null;
      })
      .filter((member) => member !== null) as Member[];
  };

  return {
    ...filterData(structuredData, searchText),
    filteredMembers: {
      current: filterTimeMembers(membersData?.current || {}),
      future: filterTimeMembers(membersData?.future || {}),
      previous: filterTimeMembers(membersData?.previous || {}),
    },
  };
}

function filterSites(
  structuredData: any | null,
  sitesData: any[],
  searchText: string
) {
  const lowercasedSearchText = (searchText || '').toLowerCase();

  const filteredSites = (sitesData || []).filter(
    (site) =>
      (site?.siteName || site?.name || '')
        .toLowerCase()
        .includes(lowercasedSearchText) ||
      (site?.pod || '').toLowerCase().includes(lowercasedSearchText) ||
      (site?.externalId || '').toLowerCase().includes(lowercasedSearchText) ||
      (site?.id || site?.flexoSiteId || '')
        .toLowerCase()
        .includes(lowercasedSearchText)
  );

  return {
    ...filterData(structuredData, searchText),
    filteredSites: filteredSites as any,
  };
}

function filterAdmins(adminsData: any[] = [], searchText: string) {
  const q = (searchText || '').toLowerCase().trim();

  if (!q) {
    return {
      filteredAdmins: adminsData,
    };
  }
  const filteredAdmins = (adminsData || []).filter((admin: any) =>
    adminUserMatchesSearch(admin, q)
  );

  return { filteredAdmins };
}

const EMPTY_COMMUNITIES: any[] = [];

const getMetadataCommunities = (metadata: any) =>
  [
    metadata?.communities,
    metadata?.entities?.community,
    metadata?.entities?.communities,
    metadata?.sortedEntities?.communities,
  ].find((communities) => Array.isArray(communities) && communities.length > 0) ||
  EMPTY_COMMUNITIES;

const debounce = _.debounce((fn) => fn(), 150);

function flattenStructuredData(structuredData: any): any[] {
  if (!structuredData) {
    return [];
  }
  const flatArray: any[] = [];

  flatArray.push(...structuredData.communities);
  flatArray.push(...structuredData.unassignedMembers);
  flatArray.push(...structuredData.unassignedSites);
  flatArray.push(...structuredData.unassignedEVs);
  flatArray.push(...structuredData.unassignedWallboxes);

  return flatArray;
}

export function SiteListItem(option: any, props: any) {
  const location = useLocation();
  const params = useParams();
  const pathSegment = location.pathname.split('/')[1];
  const isMobile = useIsMobile();

  const isActive = option?.isActive || option?.option?.siteId === params.id;
  const firstItem = option?.firstItem === true;

  return (
    <ListItem
      key={`site-${option?.option?.siteId || 'unknown'}`}
      className={`${styles.Autocomplete__List__Item} ${
        firstItem && styles.Autocomplete__List__Item__First
      }`}
      data-selected-entity={isActive ? 'true' : undefined}
    >
      <span
        style={{
          paddingLeft: option?.padding || 24,
          cursor: 'pointer',
          color: isActive ? 'var(--hive-primary-500)' : 'inherit',
        }}
        onClick={() => {
          // Prevent navigation if clicking on the same item that's already active
          if (isActive) {
            option.toggleInput();
            return;
          }

          addRecent({
            kind: 'site',
            id: option?.option?.siteId,
            label: option?.option?.siteName || option?.option?.pod || '',
            subtitle: option?.option?.pod || option?.option?.siteExternalId,
          });
          option.toggleInput();
          navigateToSite(
            isMobile
              ? 'site'
              : pathSegment?.includes('energy')
              ? 'site-energy'
              : pathSegment?.includes('economics')
              ? 'site-economics'
              : 'site',
            option?.option?.siteId
          );
        }}
      >
        {option.searchText
          ? highlightedText({
              text: option?.option?.siteName || option?.option?.pod || '',
              searchText: option.searchText,
            })
          : ellipsizeMiddle(option?.option?.siteName || '', [20, 12, 12])}
        <br />
        <small>
          {option.searchText
            ? highlightedText({
                text:
                  option?.option?.pod || option?.option?.siteExternalId || '',
                searchText: option.searchText,
              })
            : ellipsizeMiddle(
                option?.option?.pod || option?.option?.siteExternalId || '',
                [20, 12, 12]
              )}
        </small>
      </span>
    </ListItem>
  );
}

export function AdminUsersListItem(renderProps: any) {
  const dispatch = useDispatch();
  const params = useParams();

  const {
    option,
    props,
    firstItem,
    searchText,
    toggleInput,
    listKey,
    ...liProps
  } = renderProps;

  const isActive = renderProps?.isActive || option?.userId === params.id;
  const firstItemFlag = firstItem === true;

  return (
    <ListItem
      key={listKey}
      className={`${styles.Autocomplete__List__Item} ${
        firstItemFlag && styles.Autocomplete__List__Item__First
      }`}
      data-selected-entity={isActive ? 'true' : undefined}
      {...liProps}
    >
      <span
        style={{
          paddingLeft: renderProps?.padding || 24,
          cursor: 'pointer',
          color: isActive ? 'var(--hive-primary-500)' : 'inherit',
        }}
        onClick={(e) => {
          // Stop propagation to prevent MUI Autocomplete onChange from firing
          e.stopPropagation();

          // Close the dropdown
          if (toggleInput) {
            toggleInput();
          }

          // If the admin is active: reset filter and open edit drawer
          if (option?.state === 'active') {
            dispatch(METADATA.setFilteredAdmins(null as any));
            dispatch(
              RIGHT_DRAWER.SetDrawer({
                type: 'ADMIN_UPDATE_USER',
                data: option,
              } as any)
            );
          }
          // If invited: keep the filtered list visible, just close the dropdown
        }}
      >
        {searchText
          ? highlightedText({
              text: option?.user || option?.email || option?.userId || '',
              searchText,
            })
          : ellipsizeMiddle(option?.user || '', [20, 12, 12])}
        <br />
        <small>
          {searchText
            ? highlightedText({
                text: option?.email || option?.userId || '',
                searchText,
              })
            : ellipsizeMiddle(option?.email || '', [20, 12, 12])}
        </small>
      </span>
    </ListItem>
  );
}

export function MemberListItem(option: any, props: any) {
  const metadata = useSelector((state: any) => state.metadata);
  const location = useLocation();
  const params = useParams();
  const pathSegment = location.pathname.split('/')[1];
  const isMobile = useIsMobile();

  const isActive =
    option?.isActive === true || option?.option?.memberId === params.id;

  const firstItem = option?.firstItem === true;

  return (
    <ListItem
      key={`member-${option?.option?.memberId || 'unknown'}`}
      className={`${styles.Autocomplete__List__Item} ${
        firstItem && styles.Autocomplete__List__Item__First
      }`}
      data-selected-entity={isActive ? 'true' : undefined}
    >
      <span
        style={{
          paddingLeft: option?.padding || 24,
          cursor: 'pointer',
          color: isActive ? 'var(--hive-primary-500)' : 'inherit',
        }}
        onClick={() => {
          // Prevent navigation if clicking on the same item that's already active
          if (isActive) {
            option.toggleInput();
            return;
          }

          addRecent({
            kind: 'member',
            id: option?.option?.memberId,
            label: `${option?.option?.memberFirstName || ''} ${
              option?.option?.memberLastName || ''
            }`.trim(),
            subtitle: option?.option?.memberExternalId,
          });
          option.toggleInput();
          navigateToMember(
            isMobile
              ? 'member'
              : pathSegment?.includes('energy')
              ? 'member-energy'
              : pathSegment?.includes('economics')
              ? 'member-economics'
              : 'member',
            option?.option?.memberId
          );
        }}
      >
        {option.searchText
          ? highlightedText({
              text: `${option?.option?.memberFirstName || ''} ${
                option?.option?.memberLastName || ''
              }`,
              searchText: option.searchText,
            })
          : ellipsizeMiddle(
              `${option?.option?.memberFirstName || ''} ${
                option?.option?.memberLastName || ''
              }`,
              [25, 14, 14]
            )}
        <br />
        <small>
          {option.searchText
            ? highlightedText({
                text: option?.option?.memberExternalId || '',
                searchText: option.searchText,
              })
            : ellipsizeMiddle(option?.option?.memberExternalId || '', [20, 12, 12])}
        </small>
      </span>

      {option?.option?.sites && (
        <List className={`${styles.Autocomplete__List}`}>
          {[...(option?.option?.sites || [])]
            ?.sort((a: any, b: any) => {
              return `${a.siteName}`.localeCompare(`${b.siteName}`);
            })
            .map((site: any, siteIndex: number) => (
              <SiteListItem
                key={`member-${option?.option?.memberId}-site-${
                  site?.siteId || siteIndex
                }`}
                option={site}
                props={props}
                padding={(option?.padding || 24) + 16}
                searchText={option.searchText}
                isActive={
                  site?.option?.siteId && site?.option?.siteId === params.id
                }
                toggleInput={option.toggleInput}
              />
            ))}
        </List>
      )}
    </ListItem>
  );
}

export function CommunitiesListItem(option: any, props: any) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const pathSegment = location.pathname.split('/')[1];
  const isMobile = useIsMobile();

  const isActive =
    option?.option?.communityId === params.id &&
    pathSegment?.includes('community');

  const firstItem = option?.firstItem === true;

  return (
    <ListItem
      key={`list-item-${option?.option?.communityId}`}
      className={`${styles.Autocomplete__List__Item} ${
        firstItem && styles.Autocomplete__List__Item__First
      } `}
      {...props}
      value={option.value}
      data-selected-entity={isActive ? 'true' : undefined}
    >
      <span
        key={`List-item-span-${option?.option?.communityId}`}
        style={{
          paddingLeft: option?.padding || 24,
          cursor: 'pointer',
          color: isActive ? 'var(--hive-primary-500)' : 'inherit',
        }}
        onClick={() => {
          // Prevent navigation if clicking on the same item that's already active
          if (isActive) {
            option.toggleInput();
            return;
          }

          addRecent({
            kind: 'community',
            id: option?.option?.communityId,
            label: option?.option?.communityName,
            subtitle: option?.option?.communityExternalId,
          });
          option.toggleInput();
          navigateToCommunity(
            `${
              isMobile
                ? 'community'
                : pathSegment?.includes('energy')
                ? 'community-energy'
                : pathSegment?.includes('economics')
                ? 'community-economics'
                : 'community'
            }`,
            option?.option?.communityId
          );
        }}
      >
        {option.searchText
          ? highlightedText({
              text: option?.option?.communityName || '',
              searchText: option.searchText,
            })
          : option?.option?.communityName}
        <br />
        <small>
          {option.searchText
            ? highlightedText({
                text: option?.option?.communityExternalId || '',
                searchText: option.searchText,
              })
            : option?.option?.communityExternalId}
        </small>
      </span>
      {option?.option?.members && (
        <List
          key={`list-item-community-member-${option?.option?.communityExternalId}`}
          className={`${styles.Autocomplete__List}`}
        >
          {option?.option?.members
            .sort((a: any, b: any) => {
              return `${a.memberFirstName} ${a.memberLatName}`.localeCompare(
                `${b.memberFirstName} ${b.memberLatName}`
              );
            })
            .map((member: any, memberIndex: number) => (
              <MemberListItem
                key={`community-${option?.option?.communityId}-member-${
                  member?.memberId || memberIndex
                }`}
                option={member}
                props={props}
                padding={(option?.padding || 24) + 16}
                searchText={option.searchText}
                isActive={
                  member?.option?.memberId &&
                  member?.option?.memberId === params.id
                }
                toggleInput={option.toggleInput}
              />
            ))}
        </List>
      )}
    </ListItem>
  );
}

export function AutoCompleteComponent(props: any) {
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const metadata = useSelector((state: any) => state.metadata);

  const magnifierEl = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const recentPanelRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const [recentHeight, setRecentHeight] = useState<number>(0);

  const [searchText, setSearchText] = useState('');
  const [showInput, setShowInput] = useState(false);
  // TEST: track keyboard navigation index (-1 = no selection)
  const [navIndex, setNavIndex] = useState(-1);
  const showInputRef = useRef(false);

  // Reset search state when route changes
  useEffect(() => {
    setSearchText('');
    setShowInput(false);
    setNavIndex(-1);
    inputRef.current?.blur();
    dispatch(METADATA.ResetFilter('filteredCommunities' as any));
  }, [pathSegment]);

  // Keep ref in sync so the popstate handler always sees current value
  useEffect(() => {
    showInputRef.current = showInput;
  }, [showInput]);

  // On mobile: browser back closes search instead of navigating away
  useEffect(() => {
    const handlePopState = () => {
      if (showInputRef.current) {
        // Back pressed while search is open — close search, stay on page
        setSearchText('');
        setShowInput(false);
        setNavIndex(-1);
        inputRef.current?.blur();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Build flat list of all navigable items (recents + MUI entities in render order)
  // This MUST match the exact logic of filterOptions to stay in sync with the UI
  const buildFlatList = () => {
    const items: { type: string; label: string; id?: string; kind?: string }[] =
      [];
    const term = (searchText || '').trim();

    // Helper to add communities with nested members/sites (for unfiltered view)
    const addCommunitiesFlat = (communitiesData: any[]) => {
      const sorted = [...communitiesData].sort((a: any, b: any) =>
        (a.communityName || '').localeCompare(b.communityName || '')
      );
      sorted.forEach((community: any) => {
        items.push({
          type: 'community',
          label: community.communityName || '',
          id: community.communityId,
        });

        const members = [...(community.members || [])].sort((a: any, b: any) =>
          `${a.memberFirstName} ${a.memberLatName}`.localeCompare(
            `${b.memberFirstName} ${b.memberLatName}`
          )
        );
        members.forEach((member: any) => {
          items.push({
            type: 'member',
            label: `${member.memberFirstName || ''} ${
              member.memberLastName || ''
            }`.trim(),
            id: member.memberId,
          });

          const sites = [...(member.sites || [])].sort((a: any, b: any) =>
            (a.siteName || '').localeCompare(b.siteName || '')
          );
          sites.forEach((site: any) => {
            items.push({
              type: 'site',
              label: site.siteName || site.pod || '',
              id: site.siteId,
            });
          });
        });
      });
    };

    // When filtering (user typed something), replicate filterOptions logic exactly
    if (term.length > 0) {
      const lc = term.toLowerCase();
      const { filteredStructuredData } = filterData(
        (metadata as any)?.sortedEntities || null,
        term
      ) as any;

      const communities = (filteredStructuredData?.communities || []) as any[];

      // Communities: ONLY if direct match on community fields
      communities.forEach((c: any) => {
        if (!c) return;
        const cName = (c?.communityName || '').toLowerCase();
        const cCode = (c?.communityCode || '').toLowerCase();
        const cExt = (c?.communityExternalId || '').toLowerCase();
        const cId = (c?.communityId || '').toLowerCase();
        if (
          cName.includes(lc) ||
          cCode.includes(lc) ||
          cId.includes(lc) ||
          cExt.includes(lc)
        ) {
          items.push({
            type: 'community',
            label: c.communityName || '',
            id: c.communityId,
          });
        }
      });

      // Members: ONLY if direct match on member fields
      const nestedMembers = communities.flatMap((c: any) => c?.members || []);
      const extraMembers = filteredStructuredData?.unassignedMembers || [];
      [...nestedMembers, ...extraMembers].forEach((m: any) => {
        if (!m) return;
        const fullName = `${m?.memberFirstName || ''} ${
          m?.memberLastName || ''
        }`
          .toLowerCase()
          .trim();
        const mExt = (m?.memberExternalId || '').toLowerCase();
        const mId = (m?.memberId || '').toLowerCase();
        const mEmail = (m?.email || '').toLowerCase();
        if (
          fullName.includes(lc) ||
          mExt.includes(lc) ||
          mId.includes(lc) ||
          mEmail.includes(lc)
        ) {
          // Check if already added (avoid duplicates)
          if (!items.some((i) => i.id === m.memberId)) {
            items.push({
              type: 'member',
              label: `${m.memberFirstName || ''} ${
                m.memberLastName || ''
              }`.trim(),
              id: m.memberId,
            });
          }
        }
      });

      // Sites: ONLY if direct match on site fields
      const nestedSites = nestedMembers.flatMap((m: any) => m?.sites || []);
      const extraSites = filteredStructuredData?.unassignedSites || [];
      [...nestedSites, ...extraSites].forEach((s: any) => {
        if (!s) return;
        const sName = (s?.siteName || s?.name || '').toLowerCase();
        const sPod = (s?.pod || '').toLowerCase();
        const sExt = (s?.siteExternalId || s?.externalId || '').toLowerCase();
        const sId = (s?.siteId || s?.id || '').toLowerCase();
        if (
          sName.includes(lc) ||
          sPod.includes(lc) ||
          sExt.includes(lc) ||
          sId.includes(lc)
        ) {
          if (!items.some((i) => i.id === s.siteId)) {
            items.push({
              type: 'site',
              label: s.siteName || s.pod || '',
              id: s.siteId,
            });
          }
        }
      });

      return items;
    }

    // No filter: show recents + full list
    const recentItems = getRecent();
    const hasRecents = showInput && recentItems.length > 0;
    if (hasRecents) {
      recentItems.forEach((r) => {
        items.push({
          type: 'recent',
          label: r.label || '',
          id: r.id,
          kind: r.kind,
        });
      });
    }

    addCommunitiesFlat(metadata?.sortedEntities?.communities || []);
    return items;
  };

  // Apply visual highlight to the correct DOM element based on navIndex
  const applyHighlight = (navIdx: number, flatList: any[]) => {
    // Remove all existing highlights
    document.querySelectorAll('[data-nav-highlight="true"]').forEach((el) => {
      el.removeAttribute('data-nav-highlight');
    });

    if (navIdx < 0 || navIdx >= flatList.length) return;

    const item = flatList[navIdx];
    const term = (searchText || '').trim();

    if (item.type === 'recent') {
      // Recents are in the recent panel - find by index within recents
      const recentPanel = recentPanelRef.current;
      if (recentPanel) {
        const recentIdx = navIdx; // In unfiltered view, recents are first in flatList
        const recentItems = recentPanel.querySelectorAll(
          '[class*="Recent__Searches__Item"]:not([class*="Title"])'
        );
        const targetEl = recentItems[recentIdx] as HTMLElement;
        if (targetEl) {
          targetEl.setAttribute('data-nav-highlight', 'true');
          targetEl.scrollIntoView({ behavior: 'auto', block: 'nearest' });
        }
      }
    } else {
      // MUI list items
      const listbox = document.querySelector('#autocomplete-search-listbox');
      if (!listbox) return;

      if (term.length > 0) {
        // FILTERED VIEW: items are flat, navIdx directly maps to li elements
        // All items in flatList are non-recents, so navIdx is the li index
        const allLi = listbox.querySelectorAll('li');
        const targetLi = allLi[navIdx] as HTMLElement;
        if (targetLi) {
          // Apply highlight to the direct span child, not the whole li (which may contain nested ul)
          const targetSpan = targetLi.querySelector(
            ':scope > span'
          ) as HTMLElement;
          if (targetSpan) {
            targetSpan.setAttribute('data-nav-highlight', 'true');
            targetSpan.scrollIntoView({ behavior: 'auto', block: 'nearest' });
          }
        }
      } else {
        // UNFILTERED VIEW: complex hierarchical structure
        // navIdx includes recents at the start, so offset by number of recents
        const recentsCount = flatList.filter((i) => i.type === 'recent').length;
        const muiIdx = navIdx - recentsCount;

        // In hierarchical view, find all clickable spans (direct children of li with cursor style)
        const allLi = listbox.querySelectorAll('li');
        const clickableSpans: HTMLElement[] = [];
        allLi.forEach((li) => {
          const span = li.querySelector(
            ':scope > span[style*="cursor"]'
          ) as HTMLElement;
          if (span) {
            clickableSpans.push(span);
          }
        });

        const targetSpan = clickableSpans[muiIdx];
        if (targetSpan) {
          targetSpan.setAttribute('data-nav-highlight', 'true');
          targetSpan.scrollIntoView({ behavior: 'auto', block: 'nearest' });
        }
      }
    }
  };

  // Keyboard handler for arrow up/down navigation
  const handleTestKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key !== 'ArrowDown' &&
      e.key !== 'ArrowUp' &&
      e.key !== 'Enter' &&
      e.key !== 'Escape'
    )
      return;

    const flatList = buildFlatList();

    // Handle Escape - reset navigation
    if (e.key === 'Escape') {
      setNavIndex(-1);
      applyHighlight(-1, flatList);
      return;
    }

    // Handle Enter - navigate to selected item
    if (e.key === 'Enter' && navIndex >= 0 && navIndex < flatList.length) {
      e.preventDefault();
      e.stopPropagation();
      const item = flatList[navIndex];

      // Determine navigation context
      const context = pathSegment?.includes('energy')
        ? '-energy'
        : pathSegment?.includes('economics')
        ? '-economics'
        : '';

      if (item.type === 'recent') {
        if (item.kind === 'community') {
          navigateToCommunity(`community${context}`, item.id!);
        } else if (item.kind === 'member') {
          navigateToMember(`member${context}`, item.id!);
        } else if (item.kind === 'site') {
          navigateToSite(`site${context}`, item.id!);
        }
      } else if (item.type === 'community') {
        addRecent({ kind: 'community', id: item.id!, label: item.label });
        navigateToCommunity(`community${context}`, item.id!);
      } else if (item.type === 'member') {
        addRecent({ kind: 'member', id: item.id!, label: item.label });
        navigateToMember(`member${context}`, item.id!);
      } else if (item.type === 'site') {
        addRecent({ kind: 'site', id: item.id!, label: item.label });
        navigateToSite(`site${context}`, item.id!);
      }

      toggleInput();
      return;
    }

    // Handle ArrowDown/ArrowUp
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

    // Prevent MUI from handling this
    e.preventDefault();
    e.stopPropagation();

    let newIndex = navIndex;
    if (e.key === 'ArrowDown') {
      newIndex = Math.min(navIndex + 1, flatList.length - 1);
    } else if (e.key === 'ArrowUp') {
      newIndex = Math.max(navIndex - 1, -1);
    }

    setNavIndex(newIndex);

    // Apply highlight
    requestAnimationFrame(() => {
      applyHighlight(newIndex, flatList);
    });
  };

  const selectedLabel = (() => {
    const entity: any = metadata?.selectedEntity;
    if (!entity) return '';
    if (entity?.userId) return entity.user;
    if (entity?.communityName) return entity.communityName;
    if (entity?.memberFirstName || entity?.memberLastName)
      return `${entity?.memberFirstName || ''} ${
        entity?.memberLastName || ''
      }`.trim();
    if (entity?.pod || entity?.siteName) return entity?.siteName || entity?.pod;
    return entity?.name || '';
  })();

  function isChildOf(event) {
    const parentDiv = document.getElementsByClassName(
      'MuiAutocomplete-popper'
    )[0];
    let targetElement = event.target;

    while (targetElement !== document.body) {
      if (targetElement === parentDiv) {
        return true;
      }
      targetElement = targetElement.parentNode;
    }

    return false;
  }

  // Robust inside-check for search popper/listbox
  function isInsideSearch(event: any) {
    try {
      const t = (event?.target as HTMLElement) || null;
      if (!t) return false;
      if (
        t.closest &&
        (t.closest('#search-popper') ||
          t.closest('#autocomplete-search-listbox'))
      )
        return true;
      const cls = (t.className && String(t.className)) || '';
      if (cls.includes('MuiAutocomplete')) return true;
      return isChildOf(event);
    } catch {
      return false;
    }
  }

  useOnClickOutside(containerRef, (event) => {
    try {
      if (isInsideSearch(event)) {
        return;
      }
      setShowInput(false);
    } catch (err: any) {
      console.warn(err);
    }
  });

  function toggleInput() {
    if (showInput === true) {
      // Chiusura manuale tramite icona X: resetta il testo
      setSearchText('');
      inputRef.current?.blur();
    } else {
      // Apertura: push history entry so browser back closes search
      window.history.pushState({ searchOpen: true }, '');
      // Mantieni il testo esistente, focus sull'input
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }

    setShowInput(!showInput);
  }

  // Measure recent panel height when opening with empty input
  useEffect(() => {
    if (ENV?.VITE_APP_IS_WEB !== 'true') {
      return;
    }
    if (!showInput || (searchText || '') !== '') {
      if (recentHeight !== 0) setRecentHeight(0);
      return;
    }
    requestAnimationFrame(() => {
      setRecentHeight(recentPanelRef.current?.offsetHeight || 0);
    });
  }, [showInput, searchText]);

  // Auto-scroll to selected entity when popper opens (empty state).
  // Uses rAF polling instead of a fixed timeout so it naturally syncs
  // with ProgressiveListbox's batched rendering.
  useEffect(() => {
    if (ENV?.VITE_APP_IS_WEB !== 'true') {
      return;
    }
    if (!showInput || (searchText || '') !== '') return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 60; // ~1 s at 60 fps

    const tryScroll = () => {
      if (cancelled) return;

      const list =
        (listboxRef.current as unknown as HTMLElement) ||
        (document
          .getElementById('search-popper')
          ?.querySelector('.MuiAutocomplete-listbox') as HTMLElement | null);

      const target = list?.querySelector(
        '[data-selected-entity="true"]'
      ) as HTMLElement | null;

      if (!target) {
        if (++attempts < maxAttempts) {
          requestAnimationFrame(tryScroll);
        }
        return;
      }

      const header = recentPanelRef.current;
      const headerHeight = header ? header.offsetHeight : 0;
      const visualOffset = headerHeight + 8;

      requestAnimationFrame(() => {
        if (cancelled) return;
        try {
          target.style.scrollMarginTop = `${visualOffset}px`;
        } catch (err: any) {
          console.warn(err);
        }
        target.scrollIntoView({ block: 'nearest' });
      });
    };

    requestAnimationFrame(tryScroll);

    return () => {
      cancelled = true;
    };
  }, [showInput, searchText, recentHeight]);

  function sortByEntityTypes(entities: any) {
    let optionType = '';
    if (entities?.[0]?.userId) {
      optionType = 'admin';
    }
    if (entities?.[0]?.memberId) {
      optionType = 'member';
    }
    if (entities?.[0]?.communityName) {
      optionType = 'community';
    }
    if (entities?.[0]?.siteName) {
      optionType = 'site';
    }

    try {
      return entities.sort((a: any, b: any) => {
        switch (optionType) {
          case 'admin':
            return a.user.localeCompare(b.user);
            break;
          case 'community':
            return a.communityName.localeCompare(b.communityName);
            break;
          case 'member':
            return `${a.memberFirstName} ${a.memberLatName}`.localeCompare(
              `${b.memberFirstName} ${b.memberLatName}`
            );
            break;
          case 'site':
            return `${a?.siteName || a?.pod}`.localeCompare(
              `${b?.siteName || b?.pod}`
            );
            break;
          default:
            return a.id.localeCompare(b.id);
        }
      });
    } catch (err: any) {
      console.warn(err);
      return entities;
    }
  }

  const isOpen = showInput || isMobile === true;

  return (
    <div
      ref={containerRef}
      className={`${styles.Search__Container} ${
        isOpen
          ? styles.Search__Container__Active
          : styles.Search__Container__InActive
      }`}
    >
      <div
        className={`${styles.Autocomplete__Wrapper} ${
          isOpen && styles.Autocomplete__Wrapper__Active
        }`}
      >
        {isOpen && (
          <Autocomplete
            // disablePortal
            id="autocomplete-search"
            open={showInput}
            // Prevent auto-close on click outside
            onClose={(_event, _reason) => {
              // Do nothing - disable automatic close behavior
            }}
            inputValue={searchText}
            onInputChange={(_event, value) => {
              setSearchText(value || '');
              setNavIndex(-1); // Reset navigation when user types
            }}
            options={sortByEntityTypes(
              flattenStructuredData(metadata?.sortedEntities || null) || []
            )}
            getOptionLabel={(option: any) =>
              option?.communityName ||
              option?.name ||
              option?.siteName ||
              option?.evId ||
              option?.wallboxId ||
              ''
            }
            filterOptions={(options, { inputValue }) => {
              const term = (inputValue || '').trim();
              if (term.length > 0) {
                // Build flat suggestions across communities, members, and sites
                const { filteredStructuredData } = filterData(
                  (metadata as any)?.sortedEntities || null,
                  term
                ) as any;

                const suggestions: any[] = [];
                const addUnique = (arr: any[], item: any, key: string) => {
                  if (!item || !item[key]) return;
                  if (!arr.some((x) => x[key] === item[key])) arr.push(item);
                };
                const lc = term.toLowerCase();


                const communities = (filteredStructuredData?.communities ||
                  []) as any[];
                // Communities: include ONLY if community fields directly match (not just because children matched)
                communities.forEach((c) => {
                  if (!c) return;
                  const cName = (c?.communityName || '').toLowerCase();
                  const cCode = (c?.communityCode || '').toLowerCase();
                  const cExt = (c?.communityExternalId || '').toLowerCase();
                  const cId = (c?.communityId || '').toLowerCase();
                  const directMatch =
                    cName.includes(lc) ||
                    cCode.includes(lc) ||
                    cId.includes(lc) ||
                    cExt.includes(lc);
                  if (directMatch) {
                    addUnique(
                      suggestions,
                      {
                        communityId: c.communityId,
                        communityName: c.communityName,
                        communityExternalId: c.communityExternalId,
                      },
                      'communityId'
                    );
                  }
                });

                // Members: include ONLY if member fields directly match
                const nestedMembers = communities.flatMap(
                  (c) => c?.members || []
                );
                const extraMembers =
                  filteredStructuredData?.unassignedMembers || [];
                [...nestedMembers, ...extraMembers].forEach((m) => {
                  if (!m) return;
                  const fullName = `${m?.memberFirstName || ''} ${
                    m?.memberLastName || ''
                  }`
                    .toLowerCase()
                    .trim();
                  const mExt = (m?.memberExternalId || '').toLowerCase();
                  const mId = (m?.memberId || '').toLowerCase();
                  const mEmail = (m?.email || '').toLowerCase();
                  const directMatch =
                    fullName.includes(lc) ||
                    mExt.includes(lc) ||
                    mId.includes(lc) ||
                    mEmail.includes(lc);
                  if (directMatch) {
                    addUnique(
                      suggestions,
                      {
                        memberId: m.memberId,
                        memberFirstName: m.memberFirstName,
                        memberLastName: m.memberLastName,
                        memberExternalId: m.memberExternalId,
                      },
                      'memberId'
                    );
                  }
                });

                // Sites: include sites that directly match (name/pod/externalId/id)
                const nestedSites = nestedMembers.flatMap(
                  (m) => m?.sites || []
                );
                const extraSites =
                  filteredStructuredData?.unassignedSites || [];
                [...nestedSites, ...extraSites].forEach((s) => {
                  if (!s) return;
                  const sName = (s?.siteName || s?.name || '').toLowerCase();
                  const sPod = (s?.pod || '').toLowerCase();
                  const sExt = (
                    s?.siteExternalId ||
                    s?.externalId ||
                    ''
                  ).toLowerCase();
                  const sId = (s?.siteId || s?.id || '').toLowerCase();
                  const directMatch =
                    sName.includes(lc) ||
                    sPod.includes(lc) ||
                    sExt.includes(lc) ||
                    sId.includes(lc);
                  if (directMatch) {
                    addUnique(
                      suggestions,
                      {
                        siteId: s.siteId,
                        siteName: s.siteName,
                        siteExternalId: s.siteExternalId,
                        pod: s.pod,
                      },
                      'siteId'
                    );
                  }
                });

                return suggestions;
              }
              // Empty state: keep current behavior (nested directory)
              return props.handleFilterOptions(options, { inputValue });
            }}
            clearOnBlur={false}
            autoHighlight
            sx={{ width: isMobile ? 180 : 260, marginTop: 1 }}
            className={`${styles.Search} `}
            PopperComponent={(popperProps: any) => {
              const { children, ...props } = popperProps;
              const recentItems = getRecent();
              const showRecent =
                showInput &&
                (searchText || '') === '' &&
                recentItems.length > 0 &&
                ENV?.VITE_APP_IS_WEB === 'true';
              return (
                <Popper
                  {...props}
                  style={{
                    width: 315,
                    borderRadius: 8,
                    paddingTop: 16,
                    cursor: 'pointer',
                  }}
                  placement="bottom-end"
                  id="search-popper"
                  className={`${
                    ENV?.VITE_APP_IS_WEB !== 'true' ? 'mobile' : ''
                  }`}
                >
                  {showRecent && (
                    <div
                      ref={recentPanelRef}
                      className={`${styles.Autocomplete__Recent__Container}`}
                    >
                      <div
                        className={`${styles.Autocomplete__Recent__Searches__Title}`}
                      >
                        <span>{t('widgets.search.recent')}</span>
                      </div>
                      {recentItems.map((r, idx) => {
                        const isLast = idx === recentItems.length - 1;
                        const className = `${
                          styles.Autocomplete__Recent__Searches__Item
                        } ${
                          isLast
                            ? styles.Autocomplete__Recent__Searches__Item__Last
                            : ''
                        }`;
                        const onClick = () => {
                          const context = pathSegment?.includes('energy')
                            ? '-energy'
                            : pathSegment?.includes('economics')
                            ? '-economics'
                            : '';

                          if (r.kind === 'community') {
                            navigateToCommunity(`community${context}`, r.id);
                          } else if (r.kind === 'member') {
                            navigateToMember(`member${context}`, r.id);
                          } else if (r.kind === 'site') {
                            navigateToSite(`site${context}`, r.id);
                          }
                          toggleInput();
                        };
                        return (
                          <div key={`${r.kind}-${r.id}`} className={className}>
                            <span onClick={onClick}>{r.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div>{children}</div>
                </Popper>
              );
            }}
            noOptionsText={
              <span className={`${styles.Autocomplete__List__Item}`}>
                {t('widgets.search.no_results')}
              </span>
            }
            slotProps={{
              paper: {
                style: {
                  paddingTop: recentHeight,
                  overflowX: 'hidden',
                },
                className: `${styles.Autocomplete__List__Paper}`,
              },
            }}
            ListboxComponent={ProgressiveListbox}
            ListboxProps={{
              ref: (node: HTMLUListElement) => {
                listboxRef.current = node;
              },
            }}
            renderOption={(props, option, { selected }) => {
              // Extract key from props to avoid "key prop being spread into JSX" warning
              const { key, ...restProps } = props;

              // Generate a unique key based on option type and ID
              const getUniqueKey = () => {
                if ((option as any)?.__kind === 'recent_header')
                  return 'recent_header';
                if ((option as any)?.__kind === 'recent') {
                  return `recent-${
                    option?.communityId ||
                    option?.memberId ||
                    option?.siteId ||
                    key
                  }`;
                }
                if (option?.communityId)
                  return `community-${option.communityId}`;
                if (option?.memberId) return `member-${option.memberId}`;
                if (option?.siteId) return `site-${option.siteId}`;
                if (option?.userId) return `admin-${option.userId}`;
                return key || `option-${uuidv4()}`;
              };

              const uniqueKey = getUniqueKey();

              if ((option as any)?.__kind === 'recent_header') {
                return (
                  <ListItem
                    key={uniqueKey}
                    {...restProps}
                    value={'__recent_header__'}
                    className={`${styles.Autocomplete__List__Item} ${styles.Autocomplete__Recent__Searches__Title}`}
                  >
                    <span>{t('widgets.search.recent')}</span>
                  </ListItem>
                );
              }

              if ((option as any)?.__kind === 'recent') {
                const hasCommunity = (option as any)?.communityId;
                const hasMember = (option as any)?.memberId;
                const hasSite = (option as any)?.siteId;
                const label = hasCommunity
                  ? option?.communityName
                  : hasMember
                  ? `${option?.memberFirstName || ''} ${
                      option?.memberLastName || ''
                    }`.trim()
                  : hasSite
                  ? option?.siteName
                  : option?.name;
                const subtitle = hasCommunity
                  ? option?.communityExternalId
                  : hasMember
                  ? option?.memberExternalId
                  : hasSite
                  ? option?.pod || option?.siteExternalId
                  : '';
                return (
                  <ListItem
                    key={uniqueKey}
                    {...restProps}
                    value={'__recent__'}
                    className={`${styles.Autocomplete__List__Item} ${
                      styles.Autocomplete__Recent__Searches__Item
                    } ${
                      option?.isLast
                        ? styles.Autocomplete__Recent__Searches__Item__Last
                        : ''
                    }`}
                  >
                    <span
                      onClick={() => {
                        if (hasCommunity) {
                          navigateToCommunity('community', option?.communityId);
                        } else if (hasMember) {
                          navigateToMember('member', option?.memberId);
                        } else if (hasSite) {
                          navigateToSite('site', option?.siteId);
                        }
                        toggleInput();
                      }}
                    >
                      {label}
                      {/* {subtitle ? (<><br /><small>{subtitle}</small></>) : null} */}
                    </span>
                  </ListItem>
                );
              }

              const inSuggestionMode = (searchText || '').length > 0;
              let optionType = '';
              if (option?.userId) {
                optionType = 'admin';
              }
              if (option?.memberId) {
                optionType = 'member';
              }
              if (option?.communityName) {
                optionType = 'community';
              }
              if (option?.siteName) {
                optionType = 'site';
              }

              // When typing, show flat suggestions (no nesting/indent), with context subtitle and highlight
              if (inSuggestionMode && optionType) {
                const onClick = () => {
                  const context = pathSegment?.includes('energy')
                    ? '-energy'
                    : pathSegment?.includes('economics')
                    ? '-economics'
                    : '';

                  if (optionType === 'community') {
                    addRecent({
                      kind: 'community',
                      id: option?.communityId,
                      label: option?.communityName,
                      subtitle: option?.communityExternalId,
                    });
                    navigateToCommunity(
                      `community${context}`,
                      option?.communityId
                    );
                  } else if (optionType === 'member') {
                    addRecent({
                      kind: 'member',
                      id: option?.memberId,
                      label: `${option?.memberFirstName || ''} ${
                        option?.memberLastName || ''
                      }`.trim(),
                      subtitle: option?.memberExternalId,
                    });
                    navigateToMember(`member${context}`, option?.memberId);
                  } else if (optionType === 'site') {
                    addRecent({
                      kind: 'site',
                      id: option?.siteId,
                      label: option?.siteName || option?.pod || '',
                      subtitle: option?.pod || option?.siteExternalId,
                    });
                    navigateToSite(`site${context}`, option?.siteId);
                  }
                  toggleInput();
                };

                const title =
                  optionType === 'community'
                    ? option?.communityName || ''
                    : optionType === 'member'
                    ? `${option?.memberFirstName || ''} ${
                        option?.memberLastName || ''
                      }`.trim()
                    : optionType === 'admin'
                    ? option?.user
                    : option?.siteName || option?.pod || '';

                const subtitle =
                  optionType === 'community'
                    ? t('widgets.search.context.community') +
                      ` ${ellipsizeMiddle(
                        option?.communityExternalId
                          ? '- ' + option?.communityExternalId
                          : '', [20, 12, 12]
                      )}`
                    : optionType === 'member'
                    ? t('widgets.search.context.member') +
                      ` ${ellipsizeMiddle(
                        (option?.memberExternalId
                          ? '- ' + option?.memberExternalId
                          : ''), [20, 12, 12]
                      )}`
                    : optionType === 'admin'
                    ? t('widgets.search.context.admin')
                    : optionType === 'site'
                    ? t('widgets.search.context.site') +
                      ` ${ellipsizeMiddle(
                        option?.pod ? '- ' + option?.pod : '', [20, 12, 12]
                      )}`
                    : '';

                return (
                  <ListItem
                    key={uniqueKey}
                    {...restProps}
                    value={option.value}
                    className={`${styles.Autocomplete__List__Item}`}
                  >
                    <span
                      style={{ paddingLeft: 16, cursor: 'pointer' }}
                      onClick={onClick}
                    >
                      {highlightedText({ text: title, searchText })}
                      <br />
                      <small>{subtitle as any}</small>
                    </span>
                  </ListItem>
                );
              }

              switch (optionType) {
                case 'community':
                  return (
                    <CommunitiesListItem
                      key={uniqueKey}
                      option={option}
                      props={restProps}
                      firstItem={true}
                      searchText={searchText}
                      toggleInput={toggleInput}
                    />
                  );
                  break;
                case 'member':
                  return (
                    <MemberListItem
                      key={uniqueKey}
                      option={option}
                      props={restProps}
                      firstItem={true}
                      searchText={searchText}
                      toggleInput={toggleInput}
                    />
                  );
                  break;
                case 'site':
                  return (
                    <SiteListItem
                      key={uniqueKey}
                      option={option}
                      props={restProps}
                      firstItem={true}
                      searchText={searchText}
                      toggleInput={toggleInput}
                    />
                  );
                  break;
                case 'admin':
                  return (
                    <AdminUsersListItem
                      key={uniqueKey}
                      option={option}
                      props={restProps}
                      firstItem={false}
                      searchText={searchText}
                      toggleInput={toggleInput}
                    />
                  );
                  break;
                default:
                  return (
                    <ListItem
                      key={uniqueKey}
                      {...restProps}
                      value={option.value}
                    >
                      <span> {ellipsizeMiddle(option?.name || '', [20, 12, 12])}</span>
                    </ListItem>
                  );
              }
            }}
            onChange={(_e, selectedOption) => {
              if (!selectedOption) return;

              // Determine navigation context based on current path
              const context = pathSegment?.includes('energy')
                ? '-energy'
                : pathSegment?.includes('economics')
                ? '-economics'
                : '';

              // Handle community selection
              if (selectedOption?.communityId) {
                addRecent({
                  kind: 'community',
                  id: selectedOption.communityId,
                  label: selectedOption.communityName,
                  subtitle: selectedOption.communityExternalId,
                });
                navigateToCommunity(
                  `community${context}`,
                  selectedOption.communityId
                );
                toggleInput();
                return;
              }

              // Handle member selection
              if (selectedOption?.memberId) {
                addRecent({
                  kind: 'member',
                  id: selectedOption.memberId,
                  label: `${selectedOption.memberFirstName || ''} ${
                    selectedOption.memberLastName || ''
                  }`.trim(),
                  subtitle: selectedOption.memberExternalId,
                });
                navigateToMember(`member${context}`, selectedOption.memberId);
                toggleInput();
                return;
              }

              // Handle site selection
              if (selectedOption?.siteId) {
                addRecent({
                  kind: 'site',
                  id: selectedOption.siteId,
                  label: selectedOption.siteName || selectedOption.pod || '',
                  subtitle: selectedOption.pod || selectedOption.siteExternalId,
                });
                navigateToSite(`site${context}`, selectedOption.siteId);
                toggleInput();
                return;
              }
            }}
            renderInput={(params) => (
              <TextField
                inputRef={inputRef}
                style={{ marginTop: -8 }}
                focused={showInput}
                {...params}
                onChange={(e) => {
                  if (e.target.value !== '' && showInput === false) {
                    setShowInput(true);
                  }
                }}
                onFocus={() => {
                  setShowInput(true);
                  setNavIndex(-1); // Reset on focus
                }}
                onKeyDown={handleTestKeyDown}
                id="autocomplete-search-input"
                className={`${styles.Search__Input__Text}`}
                label={
                  isMobile && selectedLabel ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        paddingTop: '1px',
                      }}
                    >
                      {ellipsizeMiddle(selectedLabel || '', [30, 8, 10])}
                    </Box>
                  ) : (
                    (t('widgets.search.common_placeholder') as string)
                  )
                }
                placeholder={
                  isMobile && selectedLabel
                    ? ellipsizeMiddle(selectedLabel || '', [20, 12, 12])
                    : (t('widgets.search.common_placeholder') as string)
                }
              ></TextField>
            )}
          />
        )}
      </div>
      <div
        className={`${styles.Search__Icon__Wrapper}`}
        onClick={() => {
          toggleInput();
        }}
        ref={magnifierEl}
      >
        <IconWrapper
          iconName={
            showInput === false
              ? isMobile
                ? 'chevron-down'
                : 'search'
              : 'cross'
          }
          className={`${styles.Search__Magnifier}`}
        />
      </div>
    </div>
  );
}

export function CommunitiesAutocomplete(props: any) {
  const dispatch = useDispatch();
  const metadata = useSelector((state: any) => state.metadata);
  const [loaded, setLoaded] = useState(false);

  const handleFilterOptions = (options, { inputValue }) => {
    const { filteredStructuredData, filteredCommunities } = filterCommunities(
      metadata?.sortedEntities,
      (metadata as any)?.communities,
      inputValue
    ) as any;

    const currentIds = (filteredCommunities || []).map((c: any) => c?.communityId || '').join(',');
    const prevIds = (metadata?.filteredCommunities || []).map((c: any) => c?.communityId || '').join(',');

    if (currentIds !== prevIds) {
      if (
        (metadata as any)?.communities?.length >=
        (filteredCommunities?.length || 0)
      ) {
        debounce(() =>
          dispatch(METADATA.SetFilteredCommunities(filteredCommunities))
        );
      }
    }

    return flattenStructuredData(filteredStructuredData);
  };

  useEffect(() => {
    return () => {
      if (loaded) {
        dispatch(METADATA.ResetFilter('filteredCommunities' as any));
      } else {
        setLoaded(true);
      }
    };
  }, []);

  return (
    <AutoCompleteComponent
      handleFilterOptions={handleFilterOptions}
      data={metadata?.communities || []}
    />
  );
}

export function AdminUsersAutocomplete(props: any) {
  const dispatch = useDispatch();
  const metadata = useSelector((state: any) => state.metadata);
  const admins = useSelector((state: any) => {
    const metadataState = state.metadata;
    return [
      ...(metadataState?.admins || []),
      ...(metadataState?.adminsInvited || []),
      ...(metadataState?.adminsActive || []),
    ];
  });

  const handleFilterOptions = (options: any, { inputValue }: any) => {
    const q = inputValue || '';
    if (!q) return admins || [];

    return (admins || []).filter((admin: any) =>
      adminUserMatchesSearch(admin, q, getMetadataCommunities(metadata))
    );
  };

  const handleSearchChange = (searchText: string) => {
    dispatch(METADATA.setAdminSearchText(searchText || '') as any);
  };

  useEffect(() => {
    return () => {
      // Clear search filter on unmount
      dispatch(METADATA.setAdminSearchText('') as any);
    };
  }, []);

  return (
    <AutoCompleteComponentAdmins
      handleFilterOptions={handleFilterOptions}
      onSearchChange={handleSearchChange}
      data={admins || []}
    />
  );
}

export function SitesAutocomplete(props: any) {
  const dispatch = useDispatch();
  const metadata = useSelector((state: any) => state.metadata);

  const [loaded, setLoaded] = useState(false);

  const handleFilterOptions = (options, { inputValue }) => {
    const { filteredSites } = filterSitesMobile(
      (metadata as any)?.entities.sites,
      inputValue
    ) as any;
    return filteredSites;
  };

  useEffect(() => {
    return () => {
      if (loaded) {
        dispatch(METADATA.ResetFilter('filteredSites' as any));
      } else {
        setLoaded(true);
      }
    };
  }, []);

  return (
    <AutoCompleteComponentMobile
      handleFilterOptions={handleFilterOptions}
      data={metadata?.entities || []}
    />
  );
}

export function CommunityAutocomplete(props: any) {
  const dispatch = useDispatch();
  const params = useParams();
  const metadata = useSelector((state: any) => state.metadata);
  const [loaded, setLoaded] = useState(false);

  const handleFilterOptions = (options, { inputValue }) => {
    const { filteredStructuredData, filteredMembers } = filterMembers(
      metadata?.sortedEntities,
      metadata.members,
      inputValue
    ) as any;

    const membersToIds = (obj: any) => {
      if (!obj) return '';
      const ids = (key: string) => (obj[key] || []).map((m: any) => m?.memberId || '').join(',');
      return `${ids('current')}|${ids('future')}|${ids('previous')}`;
    };
    const currentMemberIds = membersToIds(filteredMembers);
    const prevMemberIds = membersToIds(metadata?.filteredMembers);

    if (
      props?.context === 'community' &&
      currentMemberIds !== prevMemberIds
    ) {
      const filteredMembersLength = Object.values(filteredMembers || {}).reduce(
        (acc, val: any) => acc + val.length,
        0
      );
      const community = metadata?.selectedEntity?.communityId || params.id;
      const communityMembers =
        (metadata?.entities?.member || [])?.filter(
          (member: any) => member?.communityId === community
        ) || [];

      if (communityMembers?.length >= (filteredMembersLength || 0)) {
        debounce(() => dispatch(METADATA.SetFilteredMembers(filteredMembers)));
      }
    }

    // }

    return flattenStructuredData(filteredStructuredData);
  };

  useEffect(() => {
    return () => {
      if (loaded) {
        dispatch(METADATA.ResetFilter('filteredMembers' as any));
      } else {
        setLoaded(true);
      }
    };
  }, []);

  return (
    <AutoCompleteComponent
      handleFilterOptions={handleFilterOptions}
      data={(metadata?.entities as any)?.members || {}}
    />
  );
}

export function MemberAutocomplete(props: any) {
  const metadata = useSelector((state: any) => state.metadata);
  const [loaded, setLoaded] = useState(false);

  const handleFilterOptions = (options, { inputValue }) => {
    const { filteredStructuredData, filteredMembers } = filterMembers(
      metadata?.sortedEntities,
      (metadata?.entities as any)?.members,
      inputValue
    ) as any;

    // if (inputValue !== '') {

    //   debounce(() =>
    //     globalSetFiltersStore({
    //       type: FiltersStoreTypes.SetFilteredMembers,
    //       payload: {
    //         value: filteredMembers
    //       }
    //     })
    //   )

    // } else {

    //   if ( Object.values( filterMembers ).reduce((acc, val) => acc + val.length, 0) === 0) {

    //     debounce(() =>
    //       globalSetFiltersStore({
    //         type: FiltersStoreTypes.SetFilteredMembers,
    //         payload: {
    //           value: dataStore?.data?.members
    //         }
    //       })
    //     )

    //   }
    // }

    return flattenStructuredData(filteredStructuredData);
  };

  // useEffect(() => {
  //   return () => {
  //     if (loaded) {
  //       globalSetFiltersStore({
  //         type: FiltersStoreTypes.ResetFilter,
  //         payload: {
  //           value: 'filteredPayments',
  //         },
  //       });
  //     } else {
  //       setLoaded(true);
  //     }
  //   };
  // }, []);

  return (
    <AutoCompleteComponent
      handleFilterOptions={handleFilterOptions}
      data={metadata?.members || {}}
    />
  );
}

export function SiteAutocomplete(props: any) {
  const metadata = useSelector((state: any) => state.metadata);

  const handleFilterOptions = (options, { inputValue }) => {
    const { filteredStructuredData, filteredSites } = filterSites(
      metadata?.sortedEntities,
      (metadata?.entities as any)?.site,
      inputValue
    ) as any;

    // if (inputValue !== '') {

    //   debounce(() =>
    //     globalSetFiltersStore({
    //       type: FiltersStoreTypes.SetFilteredMembers,
    //       payload: {
    //         value: filteredMembers
    //       }
    //     })
    //   )

    // } else {

    //   if ( Object.values( filterMembers ).reduce((acc, val) => acc + val.length, 0) === 0) {

    //     debounce(() =>
    //       globalSetFiltersStore({
    //         type: FiltersStoreTypes.SetFilteredMembers,
    //         payload: {
    //           value: dataStore?.data?.members
    //         }
    //       })
    //     )

    //   }
    // }

    return flattenStructuredData(filteredStructuredData);
  };

  // useEffect(() => {
  //   return () => {
  //     if (loaded) {
  //       globalSetFiltersStore({
  //         type: FiltersStoreTypes.ResetFilter,
  //         payload: {
  //           value: 'filteredPayments',
  //         },
  //       });
  //     } else {
  //       setLoaded(true);
  //     }
  //   };
  // }, []);

  return (
    <AutoCompleteComponent
      handleFilterOptions={handleFilterOptions}
      data={metadata?.members || {}}
    />
  );
}

function filterSitesMobile(sitesData: any[], searchText: string) {
  const lowercasedSearchText = (searchText || '').toLowerCase();

  const filteredSites = (sitesData || []).filter((site) =>
    (site?.siteName || site?.name || '')
      .toLowerCase()
      .includes(lowercasedSearchText)
  );

  return {
    filteredSites: filteredSites as any,
  };
}
export function MobileSites(option: any, props: any) {
  const metadata = useSelector((state: any) => state.metadata);

  const isActive =
    option?.option?.siteId === (metadata.selectedEntity as Site)?.siteId;
  const firstItem = option?.firstItem === true;

  const handleClick = () => {
    // Navigate to the selected site and close the dropdown
    navigateToSite('household', option?.option.siteId);
  };

  return (
    <ListItem
      key={option.option.siteId}
      className={`${styles.Autocomplete__List__Item} ${
        firstItem && styles.Autocomplete__List__Item__First
      }`}
    >
      <span
        key={option.option.siteId}
        style={{
          paddingLeft: option?.padding || 24,
          cursor: 'pointer',
          color: isActive ? 'var(--hive-primary-500)' : 'inherit',
        }}
        onClick={handleClick} // Ensure handleClick closes the dropdown
      >
        {/* {highlightedText({
          text: option?.option?.siteName,
          searchText: option.searchText,
        })} */}
        {ellipsizeMiddle(option?.option?.siteName || '', [20, 12, 12])}
      </span>
    </ListItem>
  );
}

// Autocomplete component that handles dropdown
export function AutoCompleteComponentMobile(props: any) {
  const { t } = useTranslation();
  const metadata = useSelector((state: any) => state.metadata);

  const [searchText, setSearchText] = useState('');

  return (
    <Autocomplete
      disablePortal
      id="autocomplete"
      options={(metadata?.entities as any).sites || []}
      getOptionLabel={(option: any) => option?.siteName || ''}
      filterOptions={(options, { inputValue }) => {
        setSearchText(inputValue);
        return props.handleFilterOptions(options, { inputValue }).slice(0, 20);
      }}
      // sx={{ width: 260 }}
      className={`${styles.SearchMobile}`}
      PopperComponent={({ children, ...props }) => (
        <Popper
          {...props}
          style={{
            width: '90%',
            borderRadius: 8,
            padding: 16,
          }}
          placement="bottom-start"
        >
          {children}
        </Popper>
      )}
      noOptionsText={
        <span className={`${styles.Autocomplete__List__Item}`}>
          {t('no sites found')}
        </span>
      }
      renderOption={(props, option, { selected }) => {
        if (option?.siteName) {
          return <MobileSites option={option} searchText={searchText} />;
        }
        return (
          <ListItem {...props} value={option.value}>
            <span> {option?.name}</span>
          </ListItem>
        );
      }}
      onChange={(_e, value) => {
        props.handleFilterOptions(props.data, {
          inputValue: value?.siteName || '',
        });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {(metadata.selectedEntity as any)?.siteName ||
                (metadata?.entities as any).sites?.[0]?.siteName}{' '}
              <IconWrapper iconName="chevron-down" />
            </Box>
          }
          placeholder={''}
          InputProps={{
            ...params.InputProps,
            sx: {
              color: 'green', // Style input text color
              fontFamily: 'Source Sans Pro', // Correct property for font family
              fontWeight: 600, // camelCase for font-weight
              fontSize: '24px !important', // camelCase for font-size
              lineHeight: '32px', // camelCase for line-height
              '&::placeholder': {
                // This correctly targets the placeholder styling
                color: 'green',
                opacity: 1, // Ensure the placeholder has the same opacity as the input text
              },
            },
          }}
          InputLabelProps={{
            sx: {
              color: 'green', // Style label text color
              fontFamily: 'Source Sans Pro', // Correct property for font family
              fontWeight: 600, // camelCase for font-weight
              fontSize: '24px !important', // camelCase for font-size
              lineHeight: '32px', // camelCase for line-height
            },
          }}
        />
      )}
    />
  );
}

// Admins-focused autocomplete (simplified, flat list, no cross-entity suggestions)
export function AutoCompleteComponentAdmins(props: any) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState('');
  const [showInput, setShowInput] = useState(false);
  const [isListReady, setIsListReady] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listTimerRef = useRef<any>(null);
  const showInputRef = useRef(false);

  useEffect(() => {
    showInputRef.current = showInput;
  }, [showInput]);

  // On mobile: browser back closes search instead of navigating away
  useEffect(() => {
    const handlePopState = () => {
      if (showInputRef.current) {
        setSearchText('');
        setShowInput(false);
        setIsListReady(false);
        if (listTimerRef.current) clearTimeout(listTimerRef.current);
        inputRef.current?.blur();
        dispatch(METADATA.setFilteredAdmins(null as any));
        props.onSearchChange?.('');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  function toggleInput() {
    if (showInput === true) {
      setSearchText('');
      setIsListReady(false);
      if (listTimerRef.current) clearTimeout(listTimerRef.current);
      inputRef.current?.blur();
      // Reset filter when closing the dropdown via X button
      dispatch(METADATA.setFilteredAdmins(null as any));
      props.onSearchChange?.('');
      setShowInput(false);
    } else {
      // Push history entry so browser back closes search
      window.history.pushState({ searchOpen: true }, '');
      setShowInput(true);
      // Focus synchronously to preserve user-gesture chain (iOS Safari
      // won't open the keyboard if focus() is called inside setTimeout)
      inputRef.current?.focus();
      // Delay list rendering until input transition completes
      listTimerRef.current = setTimeout(() => setIsListReady(true), 350);
    }
  }

  useOnClickOutside(containerRef, (event) => {
    if (!showInput) return;
    // Check if click is inside MUI Autocomplete elements
    try {
      const t = event.target as HTMLElement;
      const cls = (t?.className && String(t.className)) || '';
      if (
        cls.includes('MuiAutocomplete') ||
        t?.closest?.('#autocomplete-search-listbox') ||
        t?.closest?.('.MuiAutocomplete-popper')
      ) {
        return;
      }
    } catch {
      // ignore
    }
    // Keep search open if there is active search text filtering the tables
    if (searchText) {
      setIsListReady(false);
      if (listTimerRef.current) clearTimeout(listTimerRef.current);
      return;
    }
    setShowInput(false);
    setIsListReady(false);
    if (listTimerRef.current) clearTimeout(listTimerRef.current);
  });

  return (
    <div
      ref={containerRef}
      className={`${styles.Search__Container} ${
        showInput
          ? styles.Search__Container__Active
          : styles.Search__Container__InActive
      }`}
    >
      <div
        className={`${styles.Autocomplete__Wrapper} ${
          showInput && styles.Autocomplete__Wrapper__Active
        }`}
      >
        {
          <Autocomplete
            id="autocomplete-search"
            open={isListReady}
            inputValue={searchText}
            onInputChange={(_event, value) => {
              setSearchText(value || '');
              props.onSearchChange?.(value || '');
            }}
            options={isListReady ? (props.data || []) as any[] : []}
            getOptionLabel={(option: any) =>
              option?.user || option?.email || option?.userId || ''
            }
            filterOptions={(options, { inputValue }) => {
              return props.handleFilterOptions(options, { inputValue }).slice(0, 20);
            }}
            clearOnBlur={false}
            sx={{ width: 260, marginTop: 1 }}
            className={`${styles.Search}`}
            PopperComponent={() => null}
            noOptionsText={
              <span className={`${styles.Autocomplete__List__Item}`}>
                {t('widgets.search.no_results')}
              </span>
            }
            renderOption={(renderProps, option, _state) => {
              const { key, ...restProps } = renderProps;
              const optionKey =
                option?.userId || option?.email || option?.user || key;

              return (
                <AdminUsersListItem
                  {...restProps}
                  listKey={optionKey}
                  option={option}
                  props={props}
                  firstItem={false}
                  searchText={searchText}
                  toggleInput={toggleInput}
                />
              );
            }}
            onChange={(_e, _v) => {
              // fire filter to keep filteredAdmins in sync with chosen value
              props.handleFilterOptions(props.data, {
                inputValue: (_e?.target as any)?.outerText || searchText || '',
              });
            }}
            renderInput={(params) => (
              <TextField
                inputRef={inputRef}
                style={{ marginTop: -8 }}
                focused={showInput}
                {...params}
                onFocus={() => setShowInput(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    // Apply current filter and close dropdown
                    props.handleFilterOptions(props.data, {
                      inputValue: searchText,
                    });
                    setShowInput(false);
                    inputRef.current?.blur();
                  }
                }}
                id="autocomplete-search-input"
                className={`${styles.Search__Input__Text}`}
                label={t('widgets.search.common_placeholder') as string}
                placeholder={t('widgets.search.common_placeholder') as string}
              />
            )}
          />
        }
      </div>
      <div
        className={`${styles.Search__Icon__Wrapper}`}
        onClick={() => toggleInput()}
      >
        <IconWrapper
          iconName={showInput ? 'cross' : 'search'}
          className={`${styles.Search__Magnifier}`}
        />
      </div>
    </div>
  );
}
